import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { healthService, HealthProgramGroupCalendarDto, setCurrentUser } from '../../services/health';
import { useAuth } from 'react-oidc-context';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import PageHeader from '../../components/UI/PageHeader';
import ConfirmDialog from '../../components/UI/ConfirmDialog';
import Pagination from '../../components/UI/Pagination';
import { formatDateTime } from '../../utils/formatters';
import Alert from '../../components/UI/Alert';
import { HealthProgramGroupDto } from '../../services/health/types';

const ProgramGroupCalendarsList: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useAuth();

  // Update the current user for the health service
  useEffect(() => {
    setCurrentUser(auth.user || null);
  }, [auth.user]);

  const [calendars, setCalendars] = useState<HealthProgramGroupCalendarDto[]>([]);
  const [programGroup, setProgramGroup] = useState<HealthProgramGroupDto | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [calendarToDelete, setCalendarToDelete] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [deleteStatus, setDeleteStatus] = useState<{
    status: 'success' | 'error' | null,
    message: string
  }>({
    status: null,
    message: ''
  });

  const fetchCalendars = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const response = await healthService.getProgramGroupCalendars(id, currentPage, pageSize);
      setCalendars(response.content || []);
      setTotalElements(response.total_elements || 0);
    } catch (error) {
      console.error('Error fetching program group calendars:', error);
    } finally {
      setLoading(false);
    }
  }, [id, currentPage, pageSize]);

  const fetchProgramGroup = useCallback(async () => {
    if (!id) return;
    
    try {
      const programGroupData = await healthService.getProgramGroupById(id);
      setProgramGroup(programGroupData);
    } catch (error) {
      console.error('Error fetching program group:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchCalendars();
  }, [id, currentPage, fetchCalendars]);

  useEffect(() => {
    fetchProgramGroup();
  }, [id, fetchProgramGroup]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleDeleteClick = (calendarId: string) => {
    setCalendarToDelete(calendarId);
    setShowConfirmDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!id || !calendarToDelete) return;
    
    setShowConfirmDialog(false);
    
    try {
      await healthService.deleteProgramGroupCalendar(id, calendarToDelete);
      setDeleteStatus({
        status: 'success',
        message: 'Calendar deleted successfully'
      });
      fetchCalendars(); // Refresh the list
    } catch (error) {
      console.error('Error deleting calendar:', error);
      setDeleteStatus({
        status: 'error',
        message: 'Failed to delete calendar. Please try again.'
      });
    }

    // Clear the status message after 5 seconds
    setTimeout(() => {
      setDeleteStatus({
        status: null,
        message: ''
      });
    }, 5000);
  };

  if (loading && currentPage === 1) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Program Group Calendars"
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Health" },
          { label: "Program Groups", to: "/health/program-groups" },
          { label: programGroup?.name || 'Program Group', to: `/health/program-groups/${id}` },
          { label: "Calendars" }
        ]}
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate(`/health/program-groups/${id}`)}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back to Program Group
          </Button>
        }
      />

      <div className="mb-6">
        <Button 
          variant="primary" 
          onClick={() => navigate(`/health/program-groups/${id}/calendars/create`)}
        >
          Create Calendar
        </Button>
      </div>

      {deleteStatus.status && (
        <div className="mb-4">
          <Alert
            variant={deleteStatus.status}
            dismissible
            onDismiss={() => setDeleteStatus({ status: null, message: '' })}
          >
            {deleteStatus.message}
          </Alert>
        </div>
      )}

      <Card>
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            {calendars.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No calendars found for this program group.
                <div className="mt-2">
                  <Button
                    variant="primary"
                    onClick={() => navigate(`/health/program-groups/${id}/calendars/create`)}
                  >
                    Create Calendar
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                          Timezone
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                          Start Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                          End Date
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {calendars.map((calendar) => (
                        <tr key={calendar.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {calendar.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {calendar.timezone}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {calendar.starts_in ? formatDateTime(calendar.starts_in) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {calendar.ends_in ? formatDateTime(calendar.ends_in) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end space-x-2">
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => navigate(`/health/program-groups/${id}/calendars/${calendar.id}/edit`)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDeleteClick(calendar.id || '')}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalElements > pageSize && (
                  <div className="py-3 px-6 border-t border-gray-200">
                    <Pagination
                      currentPage={currentPage}
                      totalItems={totalElements}
                      pageSize={pageSize}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Card>

      <ConfirmDialog
        isOpen={showConfirmDialog}
        title="Delete Calendar"
        message="Are you sure you want to delete this calendar? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowConfirmDialog(false)}
      />
    </div>
  );
};

export default ProgramGroupCalendarsList;